import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const STRONG_PASSWORD_CHECK = {
  lowerCaseLetter: hasLowerCaseLetter,
  upperCaseLetter: hasUpperCaseLetter,
  number: hasNumber,
  specialChar: hasSpecialChar,
} as const;

export interface PasswordStrength {
  requirementsMet: number;
  minRequirementsMet: number;
  value: string;
  requirements: Record<
    keyof typeof STRONG_PASSWORD_CHECK,
    {
      key: keyof typeof STRONG_PASSWORD_CHECK;
      value: boolean;
    }
  >;
}

export function getPasswordStrength(value: string): PasswordStrength {
  // 3 out of 4 requirements met https://auth0.com/docs/connections/database/password-strength
  // a lower-case letter, an upper-case letter, a number, a special character (such as !@#$%^&*).
  // In addition to min 8 char
  const strongPasswordDict: PasswordStrength['requirements'] = {
    lowerCaseLetter: { key: 'lowerCaseLetter', value: STRONG_PASSWORD_CHECK.lowerCaseLetter(value) },
    upperCaseLetter: { key: 'upperCaseLetter', value: STRONG_PASSWORD_CHECK.upperCaseLetter(value) },
    number: { key: 'number', value: STRONG_PASSWORD_CHECK.number(value) },
    specialChar: { key: 'specialChar', value: STRONG_PASSWORD_CHECK.specialChar(value) },
  };
  const requirementsMet = Object.values(strongPasswordDict).filter((x) => x.value);
  const minRequirementsMet = 3;
  return {
    requirementsMet: requirementsMet.length,
    minRequirementsMet,
    value: value,
    requirements: strongPasswordDict,
  };
}

export function strongPasswordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const passwordStrengthCheck = getPasswordStrength(control.value);
    return passwordStrengthCheck.requirementsMet < passwordStrengthCheck.minRequirementsMet
      ? {
          strongPassword: {
            ...passwordStrengthCheck,
          },
        }
      : null;
  };
}

function hasLowerCaseLetter(word: string): boolean {
  return /(?=.*[a-z])/.test(word);
}
function hasUpperCaseLetter(word: string): boolean {
  return /(?=.*[A-Z])/.test(word);
}
function hasNumber(word: string): boolean {
  return /(?=.*\d)/.test(word);
}
function hasSpecialChar(word: string): boolean {
  // return /(?=.*\W])/.test(word);
  return /(?=.*[-+_!@#$%^&*.,?])/.test(word);
}
